import React from "react";
import { Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Card from "../../../../components/card";
import PaginationComponent from "../../../../components/pagination/pagination";
import SearchInputComponent from "../../../../components/searchInput/searchInput";
import { BarraAvance, esPorCobrar, montoEC, S_EC } from "../../../../components/promos/bandaEnCamino";
import { EnCamino } from "../../../../components/promos/tipos";
import { PromoRequestRow } from "../../../../services/v2/promoApplications.service";
import { cursoCorto } from "../../../../utils/cursoCorto";
import horarioBonito from "../../../../utils/horarioBonito";
import { tituloEstado } from "../../../../utils/tituloEstado";
import { HORARIO_LISTA } from "../../managment/enrollmentRequests/helpers";
import FiltrosPromos, { Filtros } from "./filtrosPromos";
import { estadoAplicada, faltanDeEntregar, fecha, monto, nombreCorto, nombreRegalo, porcentajeRegalado, regalosDe } from "./formato";
import { AdvisorShare, EXTRA_TABS, ExtraTabKey, PendingRow, PromoApplicationFiltersData, PromoApplicationRow, SOURCE_SOLICITUD_PROMO, TabCounts, TabKey, TABS } from "./interfaces";
import { AcuerdoRow, estadoAcuerdo } from "./detalleAcuerdo";
import S from "./strings";
import "./promoApplications.css";

/*
	Promociones con las piezas de Matrículas (Esteban, 2026-11-01: "que se mire igual, más sleek y más intuitivo"): botón Nueva arriba,
	todo dentro de una tarjeta, pestañas con su número y el buscador en la misma fila, la barra de filtros de pastillas, una tabla de
	verdad (tarjetas en el teléfono) y la paginación con 100/200. El clic en una fila abre su ventana de detalle con los botones.
*/

export type FilaPorAplicar = { tipo: "pendiente"; r: PendingRow } | { tipo: "devuelta"; r: PromoRequestRow };

export interface PromoApplicationsViewProps {
	movil: boolean;
	sym: string;
	tab: TabKey;
	counts: TabCounts | null;
	enCaminoTotal: number | null;
	onTab: (t: TabKey) => void;
	search: string; onSearch: (v: string) => void; onApplySearch: () => void; onClearSearch: () => void;
	filtros: Filtros; onFiltros: (f: Filtros) => void;
	options: PromoApplicationFiltersData | null;
	loading: boolean; error: string | null;
	aviso: string | null; onCerrarAviso: () => void;
	/** Aplicadas, Kits por Entregar y Anuladas. */
	aplicadas: PromoApplicationRow[] | null;
	totals: { aplicadas: number; cobrado: number; regalado: number } | null;
	/** Por Aplicar: las devueltas por Caja primero (piden algo), luego lo pendiente. */
	porAplicar: FilaPorAplicar[] | null;
	/** En Camino y Fallidas. */
	enCamino: EnCamino[] | null;
	pages: any[]; pageSizes: number[]; itemCount: { start: number; end: number; total: number }; onPageSize: (n: number) => void;
	canCreate: boolean; onNueva: () => void;
	onAbrirAplicada: (r: PromoApplicationRow) => void;
	onAbrirEnCamino: (e: EnCamino) => void;
	onAbrirPorAplicar: (f: FilaPorAplicar) => void;
	campusCountryId?: number;
	/** Acuerdos, Por Resolver, Por Aprobar y Limpieza (lo que vivía en Descuentos): solo las que tocan a quien mira. */
	extraTabs?: { key: ExtraTabKey; label: string; count: number }[];
	/** Por Resolver, Por Aprobar y Limpieza traen su propia pantalla (sin filtros ni paginación de aquí). */
	cuerpo?: React.ReactNode;
	acuerdos?: AcuerdoRow[] | null;
	acuerdosEstado?: string;
	onAcuerdosEstado?: (v: string) => void;
	onAbrirAcuerdo?: (r: AcuerdoRow) => void;
}

const gris: React.CSSProperties = { fontSize: 13, color: "var(--wa-apagado)" };
const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => <div style={gris}>{children}</div>;

/** Una asesora por renglón: dos nombres juntos hacían la columna el doble de ancha (como en Matrículas). */
const Asesoras: React.FC<{ a?: AdvisorShare[] | { name: string }[] | null; enLinea?: boolean }> = ({ a, enLinea }) => {
	const lista = (a || []).map((x) => nombreCorto(x.name));
	if (!lista.length) return <>-</>;
	return enLinea ? <>{lista.join(", ")}</> : <>{lista.map((n, i) => <div key={"as" + i} style={{ whiteSpace: "nowrap" }}>{n}</div>)}</>;
};

/** Alumno y su curso; en un 2x1, a quién se le regala el curso. */
const Alumno: React.FC<{ name: string; curso?: string | null; horario?: string | null; recibe?: string | null }> = ({ name, curso, horario, recibe }) => (
	// Ancho mínimo: con el menú lateral fijo la tabla se angosta y el nombre se partía palabra por palabra.
	<div style={{ minWidth: 190 }}>
		<b>{name || S.noName}</b>
		<Sub>{[cursoCorto(curso) || curso, horarioBonito(horario || "", HORARIO_LISTA)].filter(Boolean).join(" · ") || "-"}</Sub>
		{recibe ? <Sub><FontAwesomeIcon icon={solid("gift")} />&nbsp;{recibe}</Sub> : null}
	</div>
);

const estadoKit = (r: PromoApplicationRow) => {
	const total = regalosDe(r).length;
	const faltan = faltanDeEntregar(r).length;
	return faltan ? <Badge bg="oro" style={{ fontWeight: 500 }}>{S.nToDeliver(faltan, total)}</Badge> : <Badge bg="secondary" style={{ fontWeight: 500 }}>Entregado</Badge>;
};
const estado = (r: PromoApplicationRow, tab: TabKey) => {
	if (tab === "kits") return estadoKit(r);
	const e = estadoAplicada(r);
	return <Badge bg={e.bg} style={{ fontWeight: 500 }}>{e.texto}</Badge>;
};

const origenPA = (f: FilaPorAplicar) => f.tipo === "devuelta"
	? <Badge bg="oro" style={{ fontWeight: 500 }}>{S.returnedBadge}</Badge>
	: <Badge bg="secondary" style={{ fontWeight: 500 }}>{f.r.source === SOURCE_SOLICITUD_PROMO ? "Solicitud" : (f.r.sourceName || tituloEstado(f.r.source))}</Badge>;
const numeroPA = (f: FilaPorAplicar) => f.tipo === "devuelta" ? f.r.intentId : f.r.ticketId;
const fechaPA = (f: FilaPorAplicar) => fecha(f.tipo === "devuelta" ? f.r.returnedAt || f.r.createdAt : f.r.createdAt);

const avance = (e: EnCamino, sym: string) => {
	const s = e.currencySymbol || sym;
	if (esPorCobrar(e)) return <>{S_EC.porCobrar}<Sub>{S_EC.minToday(s, e.minToday)}</Sub></>;
	return <>{montoEC(s, e.paid)} <span style={gris}>de {montoEC(s, e.target)}</span><BarraAvance e={e} /></>;
};
const vence = (e: EnCamino, vivas: boolean) => {
	if (!vivas) return <>{fecha(e.endedAt) || fecha(e.dueDate) || "-"}{e.endedReason ? <Sub>{e.endedReason}</Sub> : null}</>;
	const d = e.daysLeft;
	return <>{fecha(e.dueDate) || "-"}{d !== null && d !== undefined ? <div><Badge bg={d <= 3 ? "oro" : "secondary"} style={{ fontWeight: 500 }}>{S_EC.dias(d)}</Badge></div> : null}</>;
};

const PromoApplicationsView: React.FC<PromoApplicationsViewProps> = (p) => {
	const enCaminoTab = p.tab === "enCamino" || p.tab === "fallidas";
	const vivas = p.tab === "enCamino";
	const tabActiva = (k: TabKey) => p.tab === k || (k === "anuladas" && p.tab === "fallidas");
	// Número solo en lo que pide trabajo (Por Aplicar, En Camino, Kits por Entregar), como «Solicitudes en Proceso» en Matrículas.
	const extra = EXTRA_TABS.includes(p.tab as ExtraTabKey);
	const cuenta = (k: TabKey): number => {
		const x = (p.extraTabs || []).find((t) => t.key === k);
		if (x) return x.count;
		return k === "enCamino" ? p.enCaminoTotal || 0 : (k === "porAplicar" || k === "kits") && p.counts ? Number(p.counts[k]) || 0 : 0;
	};
	const conFiltros = !enCaminoTab && !extra;
	const conBuscador = !extra || p.tab === "acuerdos";
	const filas = p.tab === "acuerdos" ? (p.acuerdos ?? null) : p.tab === "porAplicar" ? p.porAplicar : enCaminoTab ? p.enCamino : p.aplicadas;
	const vacio = !!filas && filas.length < 1;
	const tenue = { opacity: p.loading ? 0.6 : 1 };

	const segmento = p.tab === "anuladas" || p.tab === "fallidas" ? (
		<div className="waFmSegs" style={{ marginRight: 14 }}>
			<button type="button" className={"waFmSeg" + (p.tab === "anuladas" ? " activo" : "")} onClick={() => p.onTab("anuladas")}>{S.subAnuladas}</button>
			<button type="button" className={"waFmSeg" + (p.tab === "fallidas" ? " activo" : "")} onClick={() => p.onTab("fallidas")}>{S.subFallidas}</button>
		</div>
	) : p.tab === "acuerdos" ? (
		<div className="waFmSegs">
			<button type="button" className={"waFmSeg" + (p.acuerdosEstado === "vivos" ? " activo" : "")} onClick={() => p.onAcuerdosEstado?.("vivos")}>Vigentes</button>
			<button type="button" className={"waFmSeg" + (p.acuerdosEstado !== "vivos" ? " activo" : "")} onClick={() => p.onAcuerdosEstado?.("")}>Todos</button>
		</div>
	) : null;

	const tarjeta = (key: string, onClick: () => void, arriba: React.ReactNode, insignia: React.ReactNode, cuerpo: React.ReactNode) => (
		<div key={key} className="wpmTarjeta" style={tenue} onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}>
			<div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}><div style={{ minWidth: 0 }}>{arriba}</div><div style={{ flexShrink: 0 }}>{insignia}</div></div>
			<div style={{ fontSize: 14, marginTop: 4 }}>{cuerpo}</div>
		</div>
	);

	const lista = () => {
		if (!filas) return <div className="loader" />;
		if (p.movil) {
			if (vacio) return <div style={{ ...gris, textAlign: "center", padding: 24, fontSize: 15 }}>{S.empty}</div>;
			return (
				<div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
					{p.tab === "acuerdos" ? (p.acuerdos || []).map((a) => tarjeta("ac" + a.id, () => p.onAbrirAcuerdo?.(a),
						<Alumno name={a.name} curso={a.courseName} horario={a.scheduleName} />, estadoAcuerdo(a.statusId, a.status), <>
							<b>{a.promoName}</b>{a.roleName ? <span style={gris}> · {tituloEstado(a.roleName)}</span> : null}
							{a.omitted ? <div className="waOroTexto" style={{ fontSize: 13 }}>{a.omitted === 1 ? "1 Mes sin Tocar" : a.omitted + " Meses sin Tocar"}</div> : null}
							<Sub>#{a.id} · {fecha(a.createdAt)}{(a.advisors || []).length ? " · " : ""}<Asesoras a={a.advisors} enLinea /></Sub>
						</>)) : p.tab === "porAplicar" ? (p.porAplicar || []).map((f) => {
						const r = f.tipo === "devuelta" ? { name: f.r.student.name, curso: f.r.course, horario: f.r.scheduleName, recibe: null, monto: f.r.amount, adv: f.r.advisors }
							: { name: f.r.payer.name, curso: f.r.payer.courseName, horario: f.r.payer.scheduleText, recibe: f.r.receiver?.name, monto: f.r.amount, adv: f.r.advisors };
						return tarjeta(f.tipo + numeroPA(f) + (f.tipo === "pendiente" ? f.r.key : ""), () => p.onAbrirPorAplicar(f), <Alumno name={r.name} curso={r.curso} horario={r.horario} recibe={r.recibe} />, origenPA(f), <>
							<b>{f.r.promoName}</b>{r.monto != null ? " · " + monto(p.sym, r.monto) : ""}
							{f.tipo === "devuelta" && f.r.returnedReason ? <div className="waOroTexto" style={{ fontSize: 13 }}>{f.r.returnedReason}</div> : null}
							<Sub>{numeroPA(f) ? "#" + numeroPA(f) + " · " : ""}{fechaPA(f)}{(r.adv || []).length ? " · " : ""}<Asesoras a={r.adv} enLinea /></Sub>
						</>);
					}) : enCaminoTab ? (p.enCamino || []).map((e) => tarjeta("ec" + e.id, () => p.onAbrirEnCamino(e),
						<Alumno name={e.studentName || ""} curso={e.courseName} horario={e.scheduleText} recibe={e.giftStudentName} />,
						vence(e, vivas), <><b>{e.promoName}</b><div>{avance(e, p.sym)}</div><Sub>#{e.id} · <Asesoras a={e.advisors} enLinea /></Sub></>
					)) : (p.aplicadas || []).map((r) => tarjeta("pa" + r.id, () => p.onAbrirAplicada(r),
						<Alumno name={r.student.name} curso={r.courseName} horario={r.scheduleText} recibe={r.giftStudent?.name} />, estado(r, p.tab), <>
							<b>{r.promoName}</b> · {monto(p.sym, r.amountPaid)}<span style={gris}> · regaló {monto(p.sym, r.giftValue)}</span>
							{p.tab === "kits" ? <Sub>{faltanDeEntregar(r).map(nombreRegalo).join(" + ")}</Sub> : null}
							<Sub>#{r.id} · {fecha(r.paymentDate)}{(r.advisors || []).length ? " · " : ""}<Asesoras a={r.advisors} enLinea /></Sub>
						</>))}
				</div>
			);
		}
		const cols = p.tab === "acuerdos" ? 6 : p.tab === "porAplicar" ? 7 : enCaminoTab ? 6 : 8;
		return (
			<table className="table table-hover wpmLista" style={{ fontSize: 15, marginBottom: 0 }}>
				<thead>
					{p.tab === "acuerdos" ? (
						<tr><th style={{ width: 64, textAlign: "center" }}>{S.col.id}</th><th>{S.col.student}</th><th style={{ width: 230 }}>{S.col.promo}</th>
							<th style={{ width: 150 }}>Vendido Por</th><th style={{ width: "1%", whiteSpace: "nowrap" }}>{S.col.status}</th><th style={{ width: 100 }}>{S.col.date}</th></tr>
					) : p.tab === "porAplicar" ? (
						<tr><th style={{ width: 64, textAlign: "center" }}>{S.col.id}</th><th>{S.col.student}</th><th style={{ width: 190 }}>{S.col.promo}</th>
							<th className="wpmNum" style={{ width: 110 }}>{S.col.amount}</th><th style={{ width: 130 }}>{S.col.advisors}</th><th style={{ width: "1%", whiteSpace: "nowrap" }}>{S.col.status}</th><th style={{ width: 100 }}>{S.col.date}</th></tr>
					) : enCaminoTab ? (
						<tr><th style={{ width: 64, textAlign: "center" }}>{S.col.id}</th><th>{S.col.student}</th><th style={{ width: 190 }}>{S.col.promo}</th>
							<th style={{ width: 200 }}>{S.col.progress}</th><th style={{ width: 120 }}>{vivas ? S.col.due : S.col.ended}</th><th style={{ width: 130 }}>{S.col.advisors}</th></tr>
					) : (
						<tr><th style={{ width: 64, textAlign: "center" }}>{S.col.id}</th><th>{S.col.student}</th><th style={{ width: 190 }}>{S.col.promo}</th>
							<th className="wpmNum" style={{ width: 120 }}>{S.col.paid}</th><th className="wpmNum" style={{ width: 120 }}>{S.col.gift}</th><th style={{ width: 130 }}>{S.col.advisors}</th>
							<th style={{ width: "1%", whiteSpace: "nowrap" }}>{p.tab === "kits" ? S.col.delivery : S.col.status}</th><th style={{ width: 100 }}>{S.col.date}</th></tr>
					)}
				</thead>
				<tbody>
					{vacio ? <tr><td colSpan={cols} style={{ ...gris, textAlign: "center", padding: 24, fontSize: 15 }}>{p.tab === "acuerdos" ? "Sin acuerdos." : S.empty}</td></tr>
					: p.tab === "acuerdos" ? (p.acuerdos || []).map((a) => (
						<tr key={"ac" + a.id} style={{ cursor: "pointer", ...tenue }} onClick={() => p.onAbrirAcuerdo?.(a)}>
							<td style={{ textAlign: "center" }}>#{a.id}</td>
							<td><Alumno name={a.name} curso={a.courseName} horario={a.scheduleName} /></td>
							<td>{a.promoName}{a.roleName ? <Sub>{tituloEstado(a.roleName)}</Sub> : null}
								{a.omitted ? <div className="waOroTexto" style={{ fontSize: 13 }}>{a.omitted === 1 ? "1 Mes sin Tocar" : a.omitted + " Meses sin Tocar"}</div> : null}</td>
							<td>{(a.advisors || []).length ? <Asesoras a={a.advisors} /> : "Sin Vendedor"}</td>
							<td style={{ whiteSpace: "nowrap" }}>{estadoAcuerdo(a.statusId, a.status)}</td>
							<td>{fecha(a.createdAt)}</td>
						</tr>
					)) : p.tab === "porAplicar" ? (p.porAplicar || []).map((f) => {
						const n = numeroPA(f);
						return (
							<tr key={f.tipo + (f.tipo === "pendiente" ? f.r.key : f.r.intentId)} style={{ cursor: "pointer", ...tenue }} onClick={() => p.onAbrirPorAplicar(f)}>
								<td style={{ textAlign: "center", whiteSpace: "nowrap" }}>{n ? "#" + n : "-"}</td>
								<td>{f.tipo === "devuelta" ? <Alumno name={f.r.student.name} curso={f.r.course} horario={f.r.scheduleName} />
									: <Alumno name={f.r.payer.name} curso={f.r.payer.courseName} horario={f.r.payer.scheduleText} recibe={f.r.receiver?.name} />}</td>
								<td>{f.r.promoName}{f.tipo === "devuelta" && f.r.returnedReason ? <div className="waOroTexto" style={{ fontSize: 13 }}>{f.r.returnedReason}</div> : null}</td>
								<td className="wpmNum">{f.r.amount != null ? monto(p.sym, f.r.amount) : "-"}</td>
								<td><Asesoras a={f.r.advisors} /></td>
								<td style={{ whiteSpace: "nowrap" }}>{origenPA(f)}</td>
								<td>{fechaPA(f)}</td>
							</tr>
						);
					}) : enCaminoTab ? (p.enCamino || []).map((e) => (
						<tr key={"ec" + e.id} style={{ cursor: "pointer", ...tenue }} onClick={() => p.onAbrirEnCamino(e)}>
							<td style={{ textAlign: "center" }}>#{e.id}</td>
							<td><Alumno name={e.studentName || ""} curso={e.courseName} horario={e.scheduleText} recibe={e.giftStudentName} /></td>
							<td>{e.promoName}</td>
							<td>{avance(e, p.sym)}</td>
							<td>{vence(e, vivas)}</td>
							<td><Asesoras a={e.advisors} /></td>
						</tr>
					)) : (p.aplicadas || []).map((r) => (
						<tr key={"pa" + r.id} style={{ cursor: "pointer", ...tenue }} onClick={() => p.onAbrirAplicada(r)}>
							<td style={{ textAlign: "center" }}>#{r.id}</td>
							<td><Alumno name={r.student.name} curso={r.courseName} horario={r.scheduleText} recibe={r.giftStudent?.name} /></td>
							<td>{r.promoName}{p.tab === "kits" ? <Sub>{faltanDeEntregar(r).map(nombreRegalo).join(" + ")}</Sub> : null}</td>
							<td className="wpmNum">{monto(p.sym, r.amountPaid)}</td>
							<td className="wpmNum">{monto(p.sym, r.giftValue)}</td>
							<td><Asesoras a={r.advisors} /></td>
							<td style={{ whiteSpace: "nowrap" }}>{estado(r, p.tab)}</td>
							<td>{fecha(r.paymentDate)}</td>
						</tr>
					))}
				</tbody>
			</table>
		);
	};

	return (
		<div className="container-fluid waModulo" style={{ textAlign: "left", padding: p.movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
				<h4 style={{ margin: 0 }}>{S.title}</h4>
				{p.canCreate ? (
					<button type="button" className="btn btn-primary waBotonGrande" onClick={p.onNueva}>
						<FontAwesomeIcon icon={solid("plus")} />&nbsp;{p.movil ? S.requestShort : S.request}
					</button>
				) : null}
			</div>
			{p.error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{p.error}</div> : null}
			{p.aviso ? <div className="alert alert-success" style={{ fontSize: 15, display: "flex", justifyContent: "space-between" }}>{p.aviso}<button type="button" className="btn-close" style={{ fontSize: 10 }} onClick={p.onCerrarAviso} /></div> : null}

			<Card>
				<div style={{ padding: p.movil ? "12px 12px 0 12px" : "14px 18px 4px 18px" }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
						<ul className="nav nav-pills waTabs" style={{ gap: 4 }}>
							{[...TABS, ...(p.extraTabs || []).map((t) => ({ key: t.key as TabKey, label: t.label }))].map((t) => (
								<li className="nav-item" key={t.key}>
									<button type="button" className={"nav-link btn btn-sm " + (tabActiva(t.key) ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => p.onTab(t.key)}>
										{t.label}{cuenta(t.key) ? <span className="waTabCuenta">{cuenta(t.key)}</span> : null}
									</button>
								</li>
							))}
						</ul>
						{conBuscador ? (
							<div style={p.movil ? { flex: "1 1 100%", position: "relative" } : { minWidth: 320, position: "relative" }}>
								<SearchInputComponent search={p.search} onChangeSearch={p.onSearch} onClickSearch={p.onApplySearch} onClearSearch={p.onClearSearch} />
							</div>
						) : null}
					</div>

					{conFiltros ? (
						<FiltrosPromos valor={p.filtros} onChange={p.onFiltros} conFechas={p.tab !== "porAplicar"} etiquetaFecha="Pagadas"
							promociones={p.options?.promotions || []} asesoras={p.options?.advisors || []} antes={segmento} />
					) : segmento ? <div className="waFmBarra"><div className="waFmBarraFecha">{segmento}</div></div> : null}

					{p.tab === "aplicadas" && p.totals ? (
						<div className="wpmKpis">
							<div><small>{S.kpiApplied}</small><b>{p.totals.aplicadas}</b></div>
							<div><small>{S.kpiPaid}</small><b>{monto(p.sym, p.totals.cobrado)}</b></div>
							<div><small>{S.kpiGift}</small><b>{monto(p.sym, p.totals.regalado)}</b></div>
							<div><small>{S.kpiPct}</small><b>{porcentajeRegalado(p.totals.cobrado, p.totals.regalado)}</b></div>
						</div>
					) : null}

					{p.cuerpo ? <div className="waDescuentos" style={{ maxWidth: "none", marginTop: 14, paddingBottom: 12 }}>{p.cuerpo}</div> : (
						<div className="waScrollX" style={{ overflowX: "auto", marginTop: 14 }}>
							{lista()}
						</div>
					)}
				</div>
			</Card>
			<div className="waSobreDock" style={{ marginTop: 10 }}>
				{p.cuerpo ? null : <PaginationComponent pages={p.pages} pageSizes={p.pageSizes} itemCount={p.itemCount} onPageSizeChange={p.onPageSize} />}
			</div>
		</div>
	);
};

export default PromoApplicationsView;
